import React from 'react';
import { WavesIcon, LayoutDashboardIcon, SearchIcon, HomeIcon, UsersIcon, CalendarDaysIcon, TentIcon, BoxIcon, DumbbellIcon, CreditCardIcon, UserCheckIcon, IdCardIcon, MessageSquareIcon, FileTextIcon, ArrowUpIcon, ArrowRightIcon } from 'lucide-react';
import { useLanguage } from '../LanguageContext';
type Bilingual = {
  en: string;
  fr: string;
};
const TOP_NAV: Bilingual[] = [{
  fr: 'Tableau de bord',
  en: 'Dashboard'
}, {
  fr: 'Activités',
  en: 'Activities'
}, {
  fr: 'Réservations',
  en: 'Bookings'
}, {
  fr: 'Ressources',
  en: 'Resources'
}, {
  fr: 'Équipements',
  en: 'Equipment'
}, {
  fr: 'Communications',
  en: 'Communications'
}];
const SIDE_NAV: {
  icon: typeof HomeIcon;
  label: Bilingual;
}[] = [{
  icon: HomeIcon,
  label: {
    fr: 'Accueil',
    en: 'Home'
  }
}, {
  icon: UsersIcon,
  label: {
    fr: 'Activités',
    en: 'Activities'
  }
}, {
  icon: CalendarDaysIcon,
  label: {
    fr: 'Réservations',
    en: 'Bookings'
  }
}, {
  icon: TentIcon,
  label: {
    fr: 'Camps de jour',
    en: 'Day camps'
  }
}, {
  icon: UsersIcon,
  label: {
    fr: 'Participants',
    en: 'Participants'
  }
}, {
  icon: BoxIcon,
  label: {
    fr: 'Ressources',
    en: 'Resources'
  }
}, {
  icon: DumbbellIcon,
  label: {
    fr: 'Équipements',
    en: 'Equipment'
  }
}, {
  icon: CreditCardIcon,
  label: {
    fr: 'Paiements',
    en: 'Payments'
  }
}, {
  icon: UserCheckIcon,
  label: {
    fr: 'Présences',
    en: 'Attendance'
  }
}, {
  icon: IdCardIcon,
  label: {
    fr: 'Accès',
    en: 'Access'
  }
}, {
  icon: MessageSquareIcon,
  label: {
    fr: 'Communications',
    en: 'Communications'
  }
}, {
  icon: FileTextIcon,
  label: {
    fr: 'Rapports',
    en: 'Reports'
  }
}];
const KPIS: {
  label: Bilingual;
  value: string;
  delta: Bilingual;
  tint: string;
}[] = [{
  label: {
    fr: 'Activités aujourd’hui',
    en: 'Activities today'
  },
  value: '32',
  delta: {
    fr: '12 %',
    en: '12%'
  },
  tint: 'bg-[#F1F1FD] text-[#4F46E5]'
}, {
  label: {
    fr: 'Réservations en cours',
    en: 'Active bookings'
  },
  value: '18 / 24',
  delta: {
    fr: '',
    en: ''
  },
  tint: 'bg-[#F1F1FD] text-[#4F46E5]'
}, {
  label: {
    fr: 'Ressources disponibles',
    en: 'Available resources'
  },
  value: '27',
  delta: {
    fr: '3',
    en: '3'
  },
  tint: 'bg-[#EEFAF2] text-[#15803D]'
}, {
  label: {
    fr: 'Inscriptions cette semaine',
    en: 'Registrations this week'
  },
  value: '412',
  delta: {
    fr: '18 %',
    en: '18%'
  },
  tint: 'bg-[#F1F1FD] text-[#4F46E5]'
}];
const UPCOMING: {
  time: Bilingual;
  dot: string;
  title: Bilingual;
  place: Bilingual;
}[] = [{
  time: {
    fr: '8 h',
    en: '8:00 AM'
  },
  dot: 'bg-[#F59E0B]',
  title: {
    fr: 'Patinage libre',
    en: 'Public skating'
  },
  place: {
    fr: 'Aréna municipal',
    en: 'Municipal arena'
  }
}, {
  time: {
    fr: '10 h',
    en: '10:00 AM'
  },
  dot: 'bg-[#F59E0B]',
  title: {
    fr: 'Cours de natation',
    en: 'Swimming class'
  },
  place: {
    fr: 'Piscine municipale',
    en: 'Municipal pool'
  }
}, {
  time: {
    fr: '13 h',
    en: '1:00 PM'
  },
  dot: 'bg-[#0EA5E9]',
  title: {
    fr: 'Soccer intérieur',
    en: 'Indoor soccer'
  },
  place: {
    fr: 'Complexe sportif',
    en: 'Sports complex'
  }
}, {
  time: {
    fr: '15 h 30',
    en: '3:30 PM'
  },
  dot: 'bg-[#EF4444]',
  title: {
    fr: 'Camp de jour',
    en: 'Day camp'
  },
  place: {
    fr: 'Centre communautaire',
    en: 'Community centre'
  }
}, {
  time: {
    fr: '17 h',
    en: '5:00 PM'
  },
  dot: 'bg-[#EF4444]',
  title: {
    fr: 'Hockey mineur',
    en: 'Minor hockey'
  },
  place: {
    fr: 'Aréna municipal',
    en: 'Municipal arena'
  }
}];
export function LoisirsOpsDashboard() {
  const {
    lang
  } = useLanguage();
  const isFR = lang === 'FR';
  const pick = (v: Bilingual) => isFR ? v.fr : v.en;
  return <div className="rounded-2xl border border-[#EEF0F4] bg-white shadow-[0_20px_50px_rgba(18,18,40,0.08)] overflow-hidden" aria-hidden="true" data-id="element-4394" data-mp="2a1yx26nhxkk7">
      {/* Top bar */}
      <div className="flex items-center gap-6 px-5 h-14 border-b border-[#F0F1F5]" data-id="element-4395" data-mp="20acchjp9q94s">
        <span className="flex items-center gap-2 shrink-0" data-id="element-4396" data-mp="1w9sdsbp1qxgj">
          <WavesIcon className="w-6 h-6 text-[#4F46E5]" strokeWidth={2} data-id="element-4397" data-mp="1r94nxtck0mnw" />
          <span className="font-sans text-[15px] font-semibold tracking-[-0.02em] text-[#121212]" data-id="element-4398" data-mp="1rb8tsgu0jvqt">{isFR ? 'Loisirs ATNA' : 'ATNA Recreation'}</span>
        </span>
        <nav className="hidden md:flex items-center gap-4 flex-1 min-w-0 overflow-hidden" data-id="element-4399" data-mp="3mdvpdrpdwbzd">
          {TOP_NAV.map((n, i) => <span key={n.en} className={`flex items-center gap-1.5 font-sans text-[10.5px] whitespace-nowrap py-4 ${i === 0 ? 'text-[#121212] font-medium border-b-2 border-[#4F46E5]' : 'text-[#5B6478]'}`} data-id="element-4400" data-mp="3rdqepqtigtib" data-mpi={i}>
              {i === 0 && <LayoutDashboardIcon className="w-3 h-3 text-[#4F46E5]" data-id="element-4401" data-mp="33krw47g2w15a" />}
              {pick(n)}
            </span>)}
        </nav>
        <span className="ml-auto flex items-center gap-3 shrink-0" data-id="element-4402" data-mp="2tof1968srh8y">
          <SearchIcon className="w-4 h-4 text-[#5B6478]" data-id="element-4403" data-mp="2tq89ur2z0bjl" />
          <span className="w-8 h-8 rounded-full bg-[#EEEDFD] flex items-center justify-center font-sans text-[11px] font-semibold text-[#4F46E5]" data-id="element-4404" data-mp="2ynrmxnnxmi9y">
            CB
          </span>
        </span>
      </div>

      <div className="flex" data-id="element-4405" data-mp="wuakcyens7m2">
        {/* Sidebar */}
        <ul className="hidden lg:block w-[150px] shrink-0 border-r border-[#F0F1F5] py-3 px-2" data-id="element-4406" data-mp="2hdebfvaumt7h">
          {SIDE_NAV.map(({
          icon: Icon,
          label
        }, i) => <li key={label.en + i} className={`flex items-center gap-2 rounded-lg px-2.5 py-1.5 font-sans text-[10.5px] ${i === 0 ? 'bg-[#F1F1FD] text-[#121212] font-medium' : 'text-[#5B6478]'}`} data-id="element-4407" data-mp="2cg4e7th7xd93" data-mpi={i}>
              <Icon className={`w-3.5 h-3.5 ${i === 0 ? 'text-[#4F46E5]' : ''}`} data-id="element-4408" data-mp="303cf7z7m605b" />
              {pick(label)}
            </li>)}
        </ul>

        {/* Main */}
        <div className="flex-1 min-w-0 p-4" data-id="element-4409" data-mp="3a21qh9508mcl">
          <p className="font-sans text-[14px] font-semibold text-[#121212]" data-id="element-4410" data-mp="3a1tbnbpbn6yr">
            {isFR ? 'Aperçu des opérations' : 'Operations overview'}
          </p>
          <div className="mt-3 grid grid-cols-2 xl:grid-cols-4 gap-2.5" data-id="element-4411" data-mp="1fnsdkt1linzq">
            {KPIS.map(k => <div key={k.label.en} className="flex items-center gap-2.5 rounded-xl border border-[#F0F1F5] p-2.5" data-id="element-4412" data-mp="1apqz6lf47cul">
                <span className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${k.tint}`} data-id="element-4413" data-mp="20eeg0tyrvxn5">
                  <UsersIcon className="w-4 h-4" data-id="element-4414" data-mp="24fpy8efbhglb" />
                </span>
                <span className="min-w-0" data-id="element-4415" data-mp="29drrfs6giwai">
                  <span className="block font-sans text-[9px] text-[#5B6478] truncate" data-id="element-4416" data-mp="29cq4apc62jcf">{pick(k.label)}</span>
                  <span className="flex items-baseline gap-1.5" data-id="element-4417" data-mp="2eb1e502gotun">
                    <span className="font-sans text-[17px] font-semibold text-[#121212]" data-id="element-4418" data-mp="2jbfm6i42c3so">{k.value}</span>
                    {pick(k.delta) && <span className="flex items-center font-sans text-[10px] font-medium text-[#15803D]" data-id="element-4419" data-mp="2s4ixajr8k4k5">
                        <ArrowUpIcon className="w-2.5 h-2.5" data-id="element-4420" data-mp="2x3cccgm683mf" />
                        {pick(k.delta)}
                      </span>}
                  </span>
                </span>
              </div>)}
          </div>

          <div className="mt-3 rounded-xl border border-[#F0F1F5] p-3 max-w-[260px]" data-id="element-4421" data-mp="372kzoqmj6jzt">
            <p className="flex items-center justify-between font-sans text-[12px] font-semibold text-[#121212]" data-id="element-4422" data-mp="vfhh1eu2jp80">
              {isFR ? 'Prochaines activités' : 'Upcoming activities'}
              <ArrowRightIcon className="w-3 h-3 text-[#5B6478]" data-id="element-4423" data-mp="lfgxbrsaf73a" />
            </p>
            <p className="font-sans text-[10px] text-[#121212] font-medium mt-2" data-id="element-4424" data-mp="ghfffa6lldte">
              {isFR ? 'Jeudi 24 septembre' : 'Thursday, September 24'}
            </p>
            <ul className="mt-2 space-y-2.5" data-id="element-4425" data-mp="1e70jnkk6l19i">
              {UPCOMING.map(u => <li key={u.title.en} className="grid grid-cols-[42px_10px_1fr] items-start gap-2" data-id="element-4426" data-mp="196mnazrxzfrb">
                  <span className="font-sans text-[9.5px] text-[#5B6478] pt-0.5" data-id="element-4427" data-mp="14dbfavbe8sxt">{pick(u.time)}</span>
                  <span className={`w-2 h-2 rounded-full mt-1 ${u.dot}`} data-id="element-4428" data-mp="zfrqx2kzezs5" />
                  <span data-id="element-4429" data-mp="1ww78zqfhcr4a">
                    <span className="block font-sans text-[10.5px] font-semibold text-[#121212] leading-tight" data-id="element-4430" data-mp="1wwqicir2e76p">{pick(u.title)}</span>
                    <span className="block font-sans text-[9.5px] text-[#5B6478]" data-id="element-4431" data-mp="10he2jh6xi6yy">{pick(u.place)}</span>
                  </span>
                </li>)}
            </ul>
          </div>
        </div>
      </div>
    </div>;
}